import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FloatingField from "./FloatingField";
import Moodboards from "./Moodboards";

const SectionLabel = ({ children }) => (
    <Typography
        sx={{
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: 500,
            color: "#0d0d0e",
            letterSpacing: 0,
            fontFamily: "Inter Tight",
        }}
    >
        {children}
    </Typography>
);

// Tab 01 for design bids — project images + description (same layout as
// the execution proposal tab), plus the proposal amount input and the
// moodboards section that replaces the execution timeline.
export default function DesignProposalResponse({
    projectId,
    proposalImages = [],
    handleProposalImageUpload,
    handleDeleteProposalImage,
    proposalDescription,
    setProposalDescription,
    proposalAmount,
    setProposalAmount,
    moodboards = [],
    isDisabled = false,
}) {
    const primary = (proposalImages || []).find(
        (img) => img && img.tags == null,
    );

    const tileSx = {
        width: 200,
        height: 190,
        borderRadius: "10px",
        overflow: "hidden",
        flexShrink: 0,
    };

    // Raw value lives in state (digits + optional 2-decimal fraction); the
    // input displays it with international thousands separators.
    const formatAmountDisplay = (raw) => {
        if (!raw) return "";
        const [intPart, decPart] = String(raw).split(".");
        const formattedInt = intPart
            ? Number(intPart).toLocaleString("en-US")
            : "";
        return decPart !== undefined
            ? `${formattedInt}.${decPart}`
            : formattedInt;
    };

    const handleAmountChange = (e) => {
        const inputValue = e.target.value.replace(/,/g, "");
        // Allow empty or a positive number with up to 2 decimal places.
        if (inputValue === "" || /^\d*\.?\d{0,2}$/.test(inputValue)) {
            setProposalAmount(inputValue);
        }
    };

    return (
        <Box sx={{ margin: "1vh 0" }}>
            {/* Project images + description, side by side on md+ */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
                    gap: 2,
                    alignItems: "stretch",
                    marginBottom: "24px",
                }}
            >
                <Box>
                    <SectionLabel>Project Images</SectionLabel>
                    {primary ? (
                        <Box
                            sx={{
                                ...tileSx,
                                position: "relative",
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <img
                                src={primary.image || primary.path || primary}
                                alt="Project"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                            {!isDisabled && (
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 6,
                                        right: 6,
                                        padding: "2px",
                                        backgroundColor: "#DC2626",
                                        boxShadow:
                                            "0 1px 2px rgba(0,0,0,0.15)",
                                        "&:hover": {
                                            backgroundColor: "#B91C1C",
                                        },
                                    }}
                                    onClick={() =>
                                        handleDeleteProposalImage(primary.id)
                                    }
                                >
                                    <CloseIcon
                                        fontSize="small"
                                        sx={{ color: "#fff" }}
                                    />
                                </IconButton>
                            )}
                        </Box>
                    ) : (
                        <Box
                            role="button"
                            onClick={() =>
                                !isDisabled &&
                                document
                                    .getElementById("design-proposal-image-upload")
                                    ?.click()
                            }
                            sx={{
                                ...tileSx,
                                border: "1px dashed #D1D5DB",
                                backgroundColor: "#F9FAFB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: isDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                color: "#4D6BF6",
                                fontSize: 40,
                                lineHeight: 1,
                                fontWeight: 300,
                                "&:hover": {
                                    backgroundColor: isDisabled
                                        ? "#F9FAFB"
                                        : "#F3F4F6",
                                },
                            }}
                        >
                            +
                        </Box>
                    )}
                    <input
                        id="design-proposal-image-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        disabled={isDisabled}
                        onChange={handleProposalImageUpload}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        gap: "20px",
                        paddingTop: "30px",
                    }}
                >
                    <FloatingField
                        label="Proposal Amount"
                        type="text"
                        placeholder="e.g., 250,000.00"
                        inputProps={{ inputMode: "decimal" }}
                        value={formatAmountDisplay(proposalAmount)}
                        onChange={handleAmountChange}
                        disabled={isDisabled}
                    />
                    <FloatingField
                        label="Project Description & Approach"
                        multiline
                        rows={5}
                        placeholder="Describe your approach to this project..."
                        value={proposalDescription}
                        onChange={(e) =>
                            setProposalDescription(e.target.value)
                        }
                        disabled={isDisabled}
                    />
                </Box>
            </Box>

            {/* Moodboards */}
            <Moodboards
                projectId={projectId}
                initialMoodboards={moodboards}
                disabled={isDisabled}
            />
        </Box>
    );
}
